import { useParams } from 'react-router-dom';
import { useProducer } from '@/hooks/useProducer';
import { useProducerTeam } from '@/hooks/useProducerTeam';
import { useProducerAccess } from '@/hooks/useProducerAccess';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FarmlyAvatar } from '@/components/farmly/FarmlyAvatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useTrackOnce } from '@/lib/analytics/useTrackOnce';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MoreHorizontal, Plus, UserPlus, ChevronDown, Shield, HelpCircle, Send, Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  ROLE_LABELS,
  ROLE_DESCRIPTIONS,
  ASSIGNABLE_ROLES,
  canManageRole,
  isLastAdmin,
  type ProducerRole,
  type ProducerMember,
} from '@/types/team';
import { InviteMemberSheet } from '@/components/team/InviteMemberSheet';
import { TransferOwnerDialog } from '@/components/team/TransferOwnerDialog';
import { track } from '@/lib/analytics';

export default function ProducerTeamPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, loading: producerLoading } = useProducer(farmId || '');
  const { members, invitations, loading: teamLoading, removeMember, updateMember, transferOwner, revokeInvite, resendInvite } = useProducerTeam(farmId);
  const { role: currentUserRole } = useProducerAccess(farmId);

  const [inviteSheetOpen, setInviteSheetOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<ProducerMember | null>(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [isRoleChanging, setIsRoleChanging] = useState<string | null>(null);
  const [isResending, setIsResending] = useState<string | null>(null);

  if (!farmId) return null;

  const isLoading = producerLoading || teamLoading;
  const canManage = currentUserRole === 'admin';
  const isOwner = currentUserRole === 'admin';

  // Merge members + invitations into unified list
  const allPeople = [
    ...members.map((m) => ({
      ...m,
      displayStatus: 'active' as const,
    })),
    ...invitations
      .filter((inv) => inv.status === 'invited')
      .map((inv) => ({
        id: inv.id,
        producerId: inv.producerId,
        email: inv.email,
        name: undefined as string | undefined,
        role: inv.role,
        status: 'invited' as const,
        displayStatus: 'invited' as const,
        avatarUrl: undefined as string | undefined,
        userId: undefined as string | undefined,
        isPublic: false,
        createdAt: inv.invitedAt,
        onboardedAt: null as string | null,
        _isInvitation: true,
      })),
  ];

  const totalCount = allPeople.length;
  const headerText =
    totalCount === 1
      ? 'Du är ensam i värdteamet just nu.'
      : `Ni är ${totalCount} personer i värdteamet.`;

  // Track page view
  useTrackOnce('team_viewed', { producer_id: farmId }, farmId);

  const handleRemoveClick = (member: typeof allPeople[0]) => {
    if (member.role === 'admin' && isLastAdmin(allPeople, member.role)) {
      toast.error('Sista ägaren kan inte tas bort. Byt ägare först.');
      return;
    }
    setMemberToRemove(member as ProducerMember);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!memberToRemove) return;
    try {
      if ((memberToRemove as any)._isInvitation) {
        await revokeInvite(memberToRemove.id);
        track('team_member_removed', { producer_id: farmId, type: 'invitation' });
      } else {
        await removeMember(memberToRemove.id);
        track('team_member_removed', { producer_id: farmId, role: memberToRemove.role });
      }
    } catch {
      // Error toast shown by hook
    } finally {
      setRemoveDialogOpen(false);
      setMemberToRemove(null);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: ProducerRole) => {
    setIsRoleChanging(memberId);
    try {
      await updateMember(memberId, { role: newRole });
      track('team_role_changed', { producer_id: farmId, new_role: newRole });
    } catch {
      // Error toast shown by hook
    } finally {
      setIsRoleChanging(null);
    }
  };

  const handleResendInvite = async (invitationId: string) => {
    setIsResending(invitationId);
    try {
      await resendInvite(invitationId);
      track('team_invite_resent', { producer_id: farmId });
    } catch {
      // Error toast shown by hook
    } finally {
      setIsResending(null);
    }
  };

  const handleCopyInviteEmail = (email: string) => {
    navigator.clipboard.writeText(email).then(() => {
      toast.success('E-post kopierad');
    }).catch(() => {
      toast.error('Kunde inte kopiera');
    });
  };

  const handleTransferOwner = async (newOwnerUserId: string) => {
    track('team_owner_transfer_started', { producer_id: farmId });
    await transferOwner(newOwnerUserId);
    track('team_owner_transfer_completed', { producer_id: farmId });
  };

  const getStatusBadge = (displayStatus: 'active' | 'invited') => {
    if (displayStatus === 'active') {
      return <Badge variant="secondary" className="text-xs">Aktiv</Badge>;
    }
    return <Badge variant="outline" className="text-xs">Inbjuden</Badge>;
  };

  const getRoleBadgeVariant = (role: ProducerRole) => {
    switch (role) {
      case 'admin': return 'default' as const;
      default: return 'outline' as const;
    }
  };

  // Role dropdown for a member (only if caller can manage this role)
  const RoleChip = ({ person }: { person: typeof allPeople[0] }) => {
    const canChange = canManage && canManageRole(currentUserRole!, person.role) && !(person as any)._isInvitation;
    const lastAdmin = isLastAdmin(allPeople, person.role);

    if (!canChange || lastAdmin) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant={getRoleBadgeVariant(person.role)} className="cursor-default">
                {ROLE_LABELS[person.role]}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs max-w-48">
                {lastAdmin
                  ? 'Enda ägaren — byt ägare först för att ändra roll.'
                  : ROLE_DESCRIPTIONS[person.role]}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full border border-border hover:bg-muted/50 transition-colors disabled:opacity-50"
            disabled={isRoleChanging === person.id}
          >
            {isRoleChanging === person.id ? 'Ändrar...' : ROLE_LABELS[person.role]}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {ASSIGNABLE_ROLES.map((r) => (
            <DropdownMenuItem
              key={r}
              onClick={() => handleRoleChange(person.id, r)}
              disabled={r === person.role}
            >
              <div>
                <span className="font-medium">{ROLE_LABELS[r]}</span>
                <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[r]}</p>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const ActionsMenu = ({ person }: { person: typeof allPeople[0] }) => {
    if (!canManage) return null;
    const isInvitation = (person as any)._isInvitation;
    const canRemove = canManageRole(currentUserRole!, person.role);
    const lastAdmin = isLastAdmin(allPeople, person.role);
    if (!canRemove && !isInvitation) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isInvitation && (
            <>
              <DropdownMenuItem
                onClick={() => handleResendInvite(person.id)}
                disabled={isResending === person.id}
              >
                <Send className="h-4 w-4 mr-2" />
                {isResending === person.id ? 'Skickar...' : 'Skicka om inbjudan'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleCopyInviteEmail(person.email)}
              >
                <Copy className="h-4 w-4 mr-2" />
                Kopiera e-post
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleRemoveClick(person)}
                className="text-destructive focus:text-destructive"
              >
                Återkalla inbjudan
              </DropdownMenuItem>
            </>
          )}
          {!isInvitation && canRemove && (
            <DropdownMenuItem
              onClick={() => handleRemoveClick(person)}
              disabled={lastAdmin}
              className="text-destructive focus:text-destructive"
            >
              {lastAdmin ? 'Kan inte ta bort enda ägaren' : 'Ta bort från gården'}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // ─── Mobile Card ───
  const MemberCard = ({ person }: { person: typeof allPeople[0] }) => (
    <Card>
      <CardContent className="flex items-center gap-3 py-3 px-4">
        <FarmlyAvatar
          imageUrl={person.avatarUrl}
          initials={(person.name || person.email).slice(0, 2).toUpperCase()}
          alt={person.name || person.email}
          size="md"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{person.name || person.email}</p>
          {person.name && (
            <p className="text-xs text-muted-foreground truncate">{person.email}</p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <RoleChip person={person} />
            {getStatusBadge(person.displayStatus)}
          </div>
        </div>
        <ActionsMenu person={person} />
      </CardContent>
    </Card>
  );

  return (
    <ProducerLayout
      farmId={farmId}
      producerName={producer?.name}
      title="Värdteam"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold">Värdteam</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Medlemmar och behörigheter
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => {
              setInviteSheetOpen(true);
              track('team_invite_sent', { producer_id: farmId, action: 'open' });
            }}>
              <Plus className="h-4 w-4 mr-1.5" />
              Bjud in
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
        ) : allPeople.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="rounded-full bg-muted p-4 mb-4">
                <UserPlus className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-base font-medium mb-1">Du är ensam i värdteamet</h3>
              <p className="text-muted-foreground text-center text-sm mb-4 max-w-sm">
                Bjud in personer som ska hjälpa till att hantera gården.
              </p>
              {canManage && (
                <Button onClick={() => setInviteSheetOpen(true)}>
                  <Plus className="h-4 w-4 mr-1.5" />
                  Bjud in
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Count header */}
            <p className="text-sm text-muted-foreground">{headerText}</p>

            {/* Mobile: CardList */}
            <div className="md:hidden space-y-2">
              {allPeople.map((person) => (
                <MemberCard key={person.id} person={person} />
              ))}
            </div>

            {/* Desktop: Table */}
            <div className="hidden md:block">
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Person</TableHead>
                      <TableHead>Roll</TableHead>
                      <TableHead>Status</TableHead>
                      {canManage && <TableHead className="w-[60px]" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allPeople.map((person) => (
                      <TableRow key={person.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <FarmlyAvatar
                              imageUrl={person.avatarUrl}
                              initials={(person.name || person.email).slice(0, 2).toUpperCase()}
                              alt={person.name || person.email}
                              size="sm"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">
                                {person.name || person.email}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {person.email}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <RoleChip person={person} />
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(person.displayStatus)}
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            <ActionsMenu person={person} />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>

            {/* Owner transfer */}
            {isOwner && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={() => setTransferDialogOpen(true)}
                >
                  <Shield className="h-4 w-4 mr-1.5" />
                  Byt ägare
                </Button>
              </div>
            )}

            {/* Role help */}
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                <HelpCircle className="h-3.5 w-3.5" />
                Vad kan de olika rollerna?
              </summary>
              <div className="mt-3 grid gap-2 pl-5">
                {(['admin', 'editor', 'viewer'] as ProducerRole[]).map((r) => (
                  <div key={r}>
                    <span className="font-medium">{ROLE_LABELS[r]}</span>
                    <span className="text-muted-foreground"> — {ROLE_DESCRIPTIONS[r]}</span>
                  </div>
                ))}
              </div>
            </details>

            {/* Guardrail info */}
            <p className="text-xs text-muted-foreground">
              Det måste alltid finnas minst en ägare i värdteamet.
            </p>
          </>
        )}
      </div>

      {/* Invite Sheet */}
      <InviteMemberSheet
        open={inviteSheetOpen}
        onOpenChange={setInviteSheetOpen}
        producerId={farmId}
      />

      {/* Transfer Owner Dialog */}
      <TransferOwnerDialog
        open={transferDialogOpen}
        onOpenChange={setTransferDialogOpen}
        members={members}
        onConfirm={handleTransferOwner}
      />

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(memberToRemove as any)?._isInvitation ? 'Återkalla inbjudan' : 'Ta bort person från gården'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(memberToRemove as any)?._isInvitation
                ? `Är du säker på att du vill återkalla inbjudan till ${memberToRemove?.email}?`
                : `Är du säker på att du vill ta bort ${memberToRemove?.name || memberToRemove?.email} från gården? Personen förlorar all åtkomst.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(memberToRemove as any)?._isInvitation ? 'Återkalla' : 'Ta bort'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ProducerLayout>
  );
}
